/**
 * Admin Console → Coupons (gods only, SaaS with billing).
 *
 * Lists every platform coupon with its scope, value, redemptions and status;
 * a row expands into its redemption history (which workspace, who, when).
 * "New coupon" opens `CreateCouponModal`; the row menu copies the code or
 * revokes the coupon (no hard delete — history stays auditable, and credits
 * already redeemed are never clawed back).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Button,
    DataTable,
    DropdownMenu,
    ModalFeedback,
    PopoverMenu,
    SparkIcon,
    SparkModalPortal,
    TableToolbarContent,
    TagColored,
    TagGeneral,
    Text,
    TruncatedText,
    getOverlayPortalParent,
    sparkToast,
    useAnchoredPanelPosition,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { formatLongDate, formatMoney } from '@/features/billing/utils/billingFormat';
import { formatDateTime } from '@/shared/utils/helpers';
import { fetchAllWorkspaces, fetchCoupons, revokeCoupon } from '../api/adminApi';
import { COUPON_STATUS_TAG, couponStatusOf } from '../constants';
import CreateCouponModal, { NEW_COUPON_LABEL } from './CreateCouponModal';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EMPTY_CELL = '-';

export const ALL_WORKSPACES_SCOPE = 'All workspaces';
export const COPY_CODE_LABEL = 'Copy code';
export const REVOKE_LABEL = 'Revoke coupon';
export const REVOKE_TITLE = 'Revoke this coupon?';
export const REVOKE_BODY =
    'can no longer be redeemed. Credits already redeemed from it stay with their workspaces, and its history is kept here.';

export const COUPON_STATUS_FILTERS = [
    { id: 'active', label: 'Active' },
    { id: 'all', label: 'All coupons' },
    { id: 'exhausted', label: 'Fully redeemed' },
    { id: 'expired', label: 'Expired' },
    { id: 'revoked', label: 'Revoked' },
];

/** "3 / 10" with a cap, "3" without one. */
export function describeRedemptions(coupon) {
    const count = Number(coupon?.redemption_count) || 0;
    const cap = Number(coupon?.max_redemptions);
    return Number.isFinite(cap) && cap > 0 ? `${count} / ${cap}` : String(count);
}

async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

function CouponRowActions({ coupon, onRevoke }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const close = () => setOpen(false);

    useOutsideClick([wrapRef, panelRef], close, open);
    useEscapeKey(close, open);
    const panelStyle = useAnchoredPanelPosition(wrapRef, panelRef, open, 'end');

    const items = [
        {
            label: COPY_CODE_LABEL,
            prefixIconSlug: 'copy',
            showPrefixIcon: true,
            onClick: async () => {
                setOpen(false);
                const copied = await copyToClipboard(coupon.code);
                if (copied) sparkToast.success(`Copied ${coupon.code}`);
                else sparkToast.error('Could not copy the code. Select it from the table instead.');
            },
        },
    ];
    if (coupon.status === 'active') {
        items.push({
            label: REVOKE_LABEL,
            prefixIconSlug: 'circle-x',
            showPrefixIcon: true,
            danger: true,
            onClick: () => {
                setOpen(false);
                onRevoke(coupon);
            },
        });
    }

    const panel = open ? (
        <div ref={panelRef} className="settings-users__row-actions-panel" style={panelStyle}>
            <PopoverMenu tip={false} items={items} />
        </div>
    ) : null;

    return (
        <div ref={wrapRef} className="settings-users__row-actions">
            <Button
                variant="ghost"
                size="small"
                aria-label="Coupon actions"
                icon={<SparkIcon slug="dots" size="small" />}
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((prev) => !prev);
                }}
            />
            {open ? createPortal(panel, getOverlayPortalParent(wrapRef.current)) : null}
        </div>
    );
}

function StatusFilter({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const current = COUPON_STATUS_FILTERS.find((item) => item.id === value) || COUPON_STATUS_FILTERS[0];
    return (
        <div className="settings-users__filters">
            <DropdownMenu
                className="settings-users__type-filter"
                showLabel={false}
                placeholder=""
                text=""
                value={current.label}
                open={open}
                onOpenChange={setOpen}
                prefixIcon={(
                    <>
                        <SparkIcon slug="filter" size="small" />
                        <span className="settings-users__type-label">Status</span>
                        <TagGeneral label={current.label} />
                    </>
                )}
                items={COUPON_STATUS_FILTERS.map((item) => ({ label: item.label }))}
                onSelect={(item) => {
                    const next = COUPON_STATUS_FILTERS.find((option) => option.label === item.label);
                    onChange(next ? next.id : COUPON_STATUS_FILTERS[0].id);
                    setOpen(false);
                }}
            />
        </div>
    );
}

function RedemptionHistory({ coupon }) {
    const rows = Array.isArray(coupon.redemptions) ? coupon.redemptions : [];
    return (
        <div className="admin-coupon-history" data-testid={`coupon-history-${coupon.id}`}>
            <div className="admin-coupon-history__meta">
                <Text variant="detail-regular" color="secondary">
                    Created {coupon.created_at ? formatDateTime(coupon.created_at) : EMPTY_CELL} by {coupon.created_by || EMPTY_CELL}
                </Text>
                {coupon.revoked_at ? (
                    <Text variant="detail-regular" color="secondary">
                        Revoked {formatDateTime(coupon.revoked_at)} by {coupon.revoked_by || EMPTY_CELL}
                    </Text>
                ) : null}
                {coupon.note ? (
                    <Text variant="detail-regular" color="secondary">Note: {coupon.note}</Text>
                ) : null}
            </div>
            {rows.length ? (
                <DataTable
                    rowKey={(record) => `${record.workspace_id}-${record.redeemed_at}`}
                    columns={[
                        {
                            title: 'Workspace',
                            key: 'workspace',
                            dataIndex: 'workspace_name',
                            minWidth: 200,
                            flex: 1,
                            render: (value, row) => value || row.workspace_id || EMPTY_CELL,
                        },
                        { title: 'Redeemed by', key: 'redeemed_by', dataIndex: 'redeemed_by', minWidth: 220, render: (value) => value || EMPTY_CELL },
                        {
                            title: 'Redeemed',
                            key: 'redeemed_at',
                            dataIndex: 'redeemed_at',
                            width: 170,
                            render: (value) => (value ? formatDateTime(value) : EMPTY_CELL),
                        },
                    ]}
                    dataSource={rows}
                    disableHoverBackground
                    pagination={null}
                />
            ) : (
                <Text variant="base-regular" color="tertiary">Not redeemed yet</Text>
            )}
        </div>
    );
}

export default function CouponsAdmin() {
    const [coupons, setCoupons] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [statusFilter, setStatusFilter] = useState(COUPON_STATUS_FILTERS[0].id);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [createOpen, setCreateOpen] = useState(false);
    const [revoking, setRevoking] = useState(null);
    const [revokeBusy, setRevokeBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        const [couponsResult, workspacesResult] = await Promise.all([fetchCoupons(), fetchAllWorkspaces()]);
        if (!couponsResult.ok) {
            setCoupons([]);
            setLoadError(couponsResult.error);
        } else {
            setCoupons(Array.isArray(couponsResult.data?.coupons) ? couponsResult.data.coupons : []);
        }
        // The workspace list only feeds the "one workspace" picker; the coupons table stands on its own.
        setWorkspaces(workspacesResult.ok && Array.isArray(workspacesResult.data?.workspaces) ? workspacesResult.data.workspaces : []);
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, pageSize]);

    const filtered = useMemo(
        () => (statusFilter === 'all' ? coupons : coupons.filter((coupon) => couponStatusOf(coupon) === statusFilter)),
        [coupons, statusFilter],
    );
    const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

    const confirmRevoke = async () => {
        if (!revoking || revokeBusy) return;
        setRevokeBusy(true);
        const result = await revokeCoupon(revoking.id);
        setRevokeBusy(false);
        if (!result.ok) {
            sparkToast.error(result.error);
            return;
        }
        sparkToast.success(`Coupon ${revoking.code} was revoked`);
        setRevoking(null);
        const updated = result.data?.coupon;
        if (updated?.id) setCoupons((prev) => prev.map((coupon) => (coupon.id === updated.id ? updated : coupon)));
        else load();
    };

    const columns = [
        {
            title: 'Code',
            key: 'code',
            dataIndex: 'code',
            minWidth: 160,
            render: (value) => <span className="admin-detail-mono">{value}</span>,
        },
        {
            title: 'Value',
            key: 'amount_cents',
            dataIndex: 'amount_cents',
            width: 90,
            render: (value) => formatMoney(value, 'usd') || EMPTY_CELL,
        },
        {
            title: 'Scope',
            key: 'scope',
            dataIndex: 'workspace_id',
            minWidth: 150,
            flex: 1,
            render: (_, coupon) => {
                if (!coupon.workspace_id) return ALL_WORKSPACES_SCOPE;
                const name = coupon.workspace_name || coupon.workspace_id;
                return <TruncatedText text={name} tooltipText={name} maxWidth="100%" />;
            },
        },
        {
            title: 'Redemptions',
            key: 'redemptions',
            dataIndex: 'redemption_count',
            width: 110,
            render: (_, coupon) => describeRedemptions(coupon),
        },
        {
            title: 'Expires',
            key: 'expires_at',
            dataIndex: 'expires_at',
            width: 120,
            render: (value) => (value ? formatLongDate(value) : 'Never'),
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            width: 130,
            render: (_, coupon) => {
                const tag = COUPON_STATUS_TAG[couponStatusOf(coupon)] || COUPON_STATUS_TAG.active;
                return <TagColored label={tag.label} variant={tag.variant} />;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 56,
            render: (_, coupon) => <CouponRowActions coupon={coupon} onRevoke={setRevoking} />,
        },
    ];

    const emptyText = loading
        ? 'Loading coupons...'
        : loadError || (statusFilter === 'all' ? 'No coupons yet' : 'No coupons with this status');

    return (
        <>
            <SettingsSectionCard
                iconSlug="tag"
                title="Coupons"
                headerExtra={(
                    <>
                        <Button variant="tertiary" size="medium" disabled={loading} onClick={load}>
                            Refresh
                        </Button>
                        <Button variant="primary" size="medium" onClick={() => setCreateOpen(true)}>
                            {NEW_COUPON_LABEL}
                        </Button>
                    </>
                )}
            >
                <div className="settings-users">
                    <StatusFilter value={statusFilter} onChange={setStatusFilter} />
                    <div className="settings-users__table-block">
                        <div className="settings-users__toolbar">
                            <TableToolbarContent
                                title={`Coupons (${filtered.length})`}
                                showInfoIconTitle={false}
                                showSubtitle={false}
                            />
                        </div>
                        <DataTable
                            rowKey={(record) => String(record.id)}
                            columns={columns}
                            dataSource={paged}
                            disableHoverBackground
                            expandedRowRender={(record) => <RedemptionHistory coupon={record} />}
                            pagination={{
                                current: page,
                                pageSize,
                                total: filtered.length,
                                pageSizeOptions: PAGE_SIZE_OPTIONS,
                                onChange: setPage,
                                onPageSizeChange: (size) => {
                                    setPageSize(size);
                                    setPage(1);
                                },
                            }}
                            emptyText={emptyText}
                        />
                    </div>
                </div>
            </SettingsSectionCard>

            <CreateCouponModal
                open={createOpen}
                workspaces={workspaces}
                onClose={() => setCreateOpen(false)}
                onCreated={(created) => {
                    setCreateOpen(false);
                    if (created?.id) {
                        setCoupons((prev) => [created, ...prev.filter((coupon) => coupon.id !== created.id)]);
                        setStatusFilter((current) => (current === 'all' || current === 'active' ? current : 'active'));
                    } else {
                        load();
                    }
                }}
            />

            {revoking ? (
                <SparkModalPortal open onClose={() => !revokeBusy && setRevoking(null)} size="text">
                    <ModalFeedback
                        feedbackType="warning"
                        title={REVOKE_TITLE}
                        content={(
                            <p className="spark-modal-text-content">
                                <b>{revoking.code}</b>
                                {' '}
                                {REVOKE_BODY}
                            </p>
                        )}
                        showOverlay={false}
                        danger
                        primaryLabel="Revoke"
                        primaryDisabled={revokeBusy}
                        tertiaryLabel="Cancel"
                        onPrimaryClick={confirmRevoke}
                        onTertiaryClick={() => !revokeBusy && setRevoking(null)}
                        onCloseClick={() => !revokeBusy && setRevoking(null)}
                    />
                </SparkModalPortal>
            ) : null}
        </>
    );
}
